import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/LoggingService';

// Single-shot guards to prevent noisy duplicate logs per page session
let HAS_LOGGED_SYNCED_FROM_DB = false;
let HAS_LOGGED_SYNCED_TO_DB = false;

export type AuthMethodType = 'email' | 'google' | 'azure' | 'linkedin_oidc' | 'apple';

const AUTH_METHOD_STORAGE_KEY = 'last_auth_method';
const PENDING_AUTH_METHOD_KEY = 'pending_auth_method';

export interface AuthMethodState {
  // Auth method tracking
  lastAuthMethod: AuthMethodType | null;
  isAuthMethodLoading: boolean;
}

export interface AuthMethodActions {
  // Auth method management
  loadAuthMethod: () => Promise<void>;
  setAuthMethod: (method: AuthMethodType) => Promise<void>;
  setPendingAuthMethod: (method: AuthMethodType) => void;
  clearPendingAuthMethod: () => void;
  handlePendingAuthMethod: () => Promise<void>;
  
  // Internal helpers
  syncAuthMethodToDatabase: (method: AuthMethodType) => Promise<void>;
}

export const createAuthMethodActions = (set: any, get: any) => ({
  // Load last auth method from localStorage and database
  loadAuthMethod: async () => {
    try {
      set({ isAuthMethodLoading: true });
      
      // First, try localStorage for immediate UI update
      const stored = typeof window !== 'undefined' 
        ? localStorage.getItem(AUTH_METHOD_STORAGE_KEY) as AuthMethodType | null
        : null;
      
      if (stored) {
        set({ lastAuthMethod: stored });
        logger.debug('AuthStore', 'Loaded auth method from localStorage', { method: stored });
      }

      // Then check database for authoritative data
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('last_auth_method')
          .eq('id', user.id)
          .single() as any;

        if (profile?.last_auth_method) {
          const dbMethod = profile.last_auth_method as AuthMethodType;
          set({ lastAuthMethod: dbMethod });
          
          // Sync localStorage with database
          if (typeof window !== 'undefined') {
            localStorage.setItem(AUTH_METHOD_STORAGE_KEY, dbMethod);
          }
          
          if (!HAS_LOGGED_SYNCED_FROM_DB) {
            logger.debug('AuthStore', 'Synced auth method from database', { method: dbMethod });
            HAS_LOGGED_SYNCED_FROM_DB = true;
          }
        }
      }
    } catch (error) {
      logger.warn('AuthStore', 'Failed to load last auth method', { error });
    } finally {
      set({ isAuthMethodLoading: false });
    }
  },

  // Set authentication method with immediate UI update and background database sync
  setAuthMethod: async (method: AuthMethodType) => {
    try {
      // Immediate UI update
      set({ lastAuthMethod: method });
      
      // Update localStorage immediately
      if (typeof window !== 'undefined') {
        localStorage.setItem(AUTH_METHOD_STORAGE_KEY, method);
      }
      
      // Background database sync
      await get().syncAuthMethodToDatabase(method);
      
      logger.info('AuthStore', 'Auth method set successfully', { method });
    } catch (error) {
      logger.error('AuthStore', 'Failed to set auth method', { error, method });
    }
  },

  // Set pending auth method for OAuth flows
  setPendingAuthMethod: (method: AuthMethodType) => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(PENDING_AUTH_METHOD_KEY, method);
        logger.debug('AuthStore', 'Set pending auth method', { method });
      }
    } catch (error) {
      logger.warn('AuthStore', 'Failed to set pending auth method', { error, method });
    }
  },

  // Clear pending auth method
  clearPendingAuthMethod: () => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(PENDING_AUTH_METHOD_KEY);
        logger.debug('AuthStore', 'Cleared pending auth method');
      }
    } catch (error) {
      logger.warn('AuthStore', 'Failed to clear pending auth method', { error });
    }
  },

  // Handle pending auth method after OAuth completion
  handlePendingAuthMethod: async () => {
    try {
      const pending = typeof window !== 'undefined' 
        ? localStorage.getItem(PENDING_AUTH_METHOD_KEY)
        : null;
        
      if (pending && ['google', 'azure', 'linkedin_oidc', 'apple'].includes(pending)) {
        await get().setAuthMethod(pending as AuthMethodType);
        logger.info('AuthStore', 'Processed pending auth method', { method: pending });
      }
      
      // Always clear the pending flag after handling
      get().clearPendingAuthMethod();
    } catch (error) {
      logger.warn('AuthStore', 'Failed to handle pending auth method', { error });
      get().clearPendingAuthMethod();
    }
  },

  // Internal helper to sync auth method to database
  syncAuthMethodToDatabase: async (method: AuthMethodType) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { error } = await supabase
          .from('user_profiles')
          .update({
            last_auth_method: method,
            last_auth_timestamp: new Date().toISOString(),
          } as any)
          .eq('id', user.id);

        if (error) {
          logger.warn('AuthStore', 'Failed to update last auth method in database', { error, method });
        } else {
          if (!HAS_LOGGED_SYNCED_TO_DB) {
            logger.debug('AuthStore', 'Successfully synced auth method to database', { method });
            HAS_LOGGED_SYNCED_TO_DB = true;
          }
        }
      }
    } catch (error) {
      logger.error('AuthStore', 'Database sync error for auth method', { error, method });
    }
  }
});
